import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { Location, SelectedGridCell, Selection, SpreadsheetSelectionSnapshot } from './ScalarDatasheet.props';

export type SelectionGesture = 'replace' | 'toggle';

// Selected cells keyed by a stable cell identity so they follow their cell when the grid is re-sorted.
export type SelectionExtras = ReadonlyMap<string, Location>;

// The logical selection as recorded when a gesture ended; positions are re-resolved against the live grid.
export interface CommittedSelection {
  identities: ReadonlyMap<string, Location>;
  rectIdentities: ReadonlySet<string>;
  rect: Selection | null;
  gesture: SelectionGesture;
}

export interface ResolvedSelection {
  positions: ReadonlyMap<string, Location>;
  rectIntact: boolean;
  snapshot: SpreadsheetSelectionSnapshot | null;
}

export interface UseSpreadsheetSelectionArgs {
  data: ICell[][];
  onCommit?: (snapshot: SpreadsheetSelectionSnapshot | null) => void;
  // Hears a selection that moved without a gesture ending, such as the press that starts one.
  onSync?: (snapshot: SpreadsheetSelectionSnapshot | null) => void;
  getCellIdentity?: (cell: ICell) => string;
}

export interface UseSpreadsheetSelectionResult {
  rect: Selection | null;
  rectIntact: boolean;
  extras: SelectionExtras;
  snapshot: SpreadsheetSelectionSnapshot | null;
  isSelected: (i: number, j: number) => boolean;
  hasMultiSelection: boolean;
  setRect: (rect: Selection | null) => void;
  /** Toggles one cell in or out of the selection; false only when that emptied the selection. */
  ctrlToggle: (i: number, j: number) => boolean;
  clearExtras: () => void;
  clearAll: () => void;
  commit: (gesture?: SelectionGesture) => void;
  selectedCells: () => SelectedGridCell[];
}

type IdentityOf = (cell: ICell) => string;

const NO_CELLS: ReadonlyMap<string, Location> = new Map();
const NO_IDENTITIES: ReadonlySet<string> = new Set();

const defaultIdentity: IdentityOf = cell => cell.key ?? '';

export const cellKey = (i: number, j: number): string => `${i}:${j}`;

// Legend cells are chrome; every other rendered cell can be selected. A sheet that blanks a cell with
// `hidden` (a value that does not apply to that column, such as issue price on common stock) still draws
// it and still lets a drag cover it, so the selection has to hold it like any other cell.
const isSelectable = (cell: ICell | undefined): cell is ICell => Boolean(cell) && !cell?.isLegend;

const byPosition = (a: Location, b: Location) => a.i - b.i || a.j - b.j;

// Orders a drag rectangle so start is the top-left corner and end the bottom-right.
export const normalizeRect = ({ start, end }: Selection): Selection => ({
  start: { i: Math.min(start.i, end.i), j: Math.min(start.j, end.j) },
  end: { i: Math.max(start.i, end.i), j: Math.max(start.j, end.j) },
});

const containsCell = (rect: Selection | null, i: number, j: number): boolean => {
  if (!rect) {
    return false;
  }
  const { start, end } = normalizeRect(rect);
  return i >= start.i && i <= end.i && j >= start.j && j <= end.j;
};

const rectLocations = (rect: Selection | null): Location[] => {
  if (!rect) {
    return [];
  }
  const {
    start: { i: top, j: left },
    end: { i: bottom, j: right },
  } = normalizeRect(rect);
  const locations: Location[] = [];
  for (let i = top; i <= bottom; i += 1) {
    for (let j = left; j <= right; j += 1) {
      locations.push({ i, j });
    }
  }
  return locations;
};

const boundingBox = (locations: Location[]): Selection => {
  const rows = locations.map(l => l.i);
  const cols = locations.map(l => l.j);
  return {
    start: { i: Math.min(...rows), j: Math.min(...cols) },
    end: { i: Math.max(...rows), j: Math.max(...cols) },
  };
};

const distinctSorted = (values: number[]): number[] => Array.from(new Set(values)).sort((a, b) => a - b);

// Turns the rectangle plus extras into unique descriptors of selectable cells, rectangle first, extras in row-major order.
export const buildDescriptors = (data: ICell[][], rect: Selection | null, extras: SelectionExtras): SelectedGridCell[] => {
  const seen = new Set<string>();
  const cells: SelectedGridCell[] = [];
  [...rectLocations(rect), ...Array.from(extras.values()).sort(byPosition)].forEach(({ i, j }) => {
    const key = cellKey(i, j);
    const cell = data[i]?.[j];
    if (seen.has(key) || !isSelectable(cell)) {
      return;
    }
    seen.add(key);
    cells.push({ cell, row: i, col: j });
  });
  return cells;
};

const identitiesIn = (data: ICell[][], rect: Selection | null, identityOf: IdentityOf): Map<string, Location> => {
  const found = new Map<string, Location>();
  buildDescriptors(data, rect, NO_CELLS).forEach(({ cell, row, col }) => {
    const id = identityOf(cell);
    if (id) {
      found.set(id, { i: row, j: col });
    }
  });
  return found;
};

const indexIdentities = (data: ICell[][], identityOf: IdentityOf): Map<string, Location> => {
  const index = new Map<string, Location>();
  data.forEach((row, i) =>
    row.forEach((cell, j) => {
      const id = isSelectable(cell) ? identityOf(cell) : '';
      if (id) {
        index.set(id, { i, j });
      }
    })
  );
  return index;
};

// Finds where each committed identity lives in the given grid and builds the snapshot from those cells;
// the committed rectangle bounds are kept only while every one of its cells is still where it was recorded.
export const resolveSelection = (
  committed: CommittedSelection | null,
  data: ICell[][],
  identityOf: IdentityOf
): ResolvedSelection => {
  if (!committed) {
    return { positions: NO_CELLS, rectIntact: true, snapshot: null };
  }
  const index = indexIdentities(data, identityOf);
  const positions = new Map<string, Location>();
  committed.identities.forEach((_, id) => {
    const at = index.get(id);
    if (at) {
      positions.set(id, at);
    }
  });
  const rectIntact = Array.from(committed.rectIdentities).every(id => {
    const was = committed.identities.get(id);
    const now = positions.get(id);
    return Boolean(was && now) && was?.i === now?.i && was?.j === now?.j;
  });
  const inRect = (id: string) => Number(committed.rectIdentities.has(id));
  const cells = Array.from(positions)
    .sort(([a, at], [b, bt]) => inRect(b) - inRect(a) || byPosition(at, bt))
    .map(([, at]) => ({ cell: data[at.i][at.j], row: at.i, col: at.j }));
  if (!cells.length) {
    return { positions, rectIntact, snapshot: null };
  }
  const { start, end } = rectIntact && committed.rect ? committed.rect : boundingBox(cells.map(c => ({ i: c.row, j: c.col })));
  return {
    positions,
    rectIntact,
    snapshot: {
      start,
      end,
      cells,
      rows: distinctSorted(cells.map(c => c.row)),
      cols: distinctSorted(cells.map(c => c.col)),
      gesture: committed.gesture,
    },
  };
};

// Two snapshots describe the same selection when the same gesture holds the same cell objects in the
// same places. Pages that rebuild their grid rows every render must not churn the snapshot's identity,
// so a cell whose state really changed has to arrive as a new cell object.
const sameSnapshot = (a: SpreadsheetSelectionSnapshot | null, b: SpreadsheetSelectionSnapshot | null): boolean => {
  if (a === b) {
    return true;
  }
  return Boolean(
    a &&
      b &&
      a.gesture === b.gesture &&
      a.start.i === b.start.i &&
      a.start.j === b.start.j &&
      a.end.i === b.end.i &&
      a.end.j === b.end.j &&
      a.cells.length === b.cells.length &&
      a.cells.every(({ cell, row, col }, i) => {
        const other = b.cells[i];
        return cell === other.cell && row === other.row && col === other.col;
      })
  );
};

const sameLocation = (a?: Location | null, b?: Location | null) => a?.i === b?.i && a?.j === b?.j;

// True when two recorded selections hold the same cells, in the same places, from the same gesture.
const sameCommitted = (a: CommittedSelection | null, b: CommittedSelection | null): boolean =>
  a === b ||
  Boolean(
    a &&
      b &&
      a.gesture === b.gesture &&
      sameLocation(a.rect?.start, b.rect?.start) &&
      sameLocation(a.rect?.end, b.rect?.end) &&
      a.identities.size === b.identities.size &&
      a.rectIdentities.size === b.rectIdentities.size &&
      Array.from(a.identities).every(([id, at]) => sameLocation(at, b.identities.get(id))) &&
      Array.from(a.rectIdentities).every(id => b.rectIdentities.has(id))
  );

const committedFrom = (
  identities: Map<string, Location>,
  rectIdentities: ReadonlySet<string>,
  rect: Selection | null,
  gesture: SelectionGesture
): CommittedSelection | null =>
  identities.size ? { identities, rectIdentities, rect: rect && normalizeRect(rect), gesture } : null;

// Tracks the datasheet's live rectangle plus the committed logical selection, resolving both against the current grid.
const useSpreadsheetSelection = ({
  data,
  onCommit,
  onSync,
  getCellIdentity = defaultIdentity,
}: UseSpreadsheetSelectionArgs): UseSpreadsheetSelectionResult => {
  const [rect, setRectState] = useState<Selection | null>(null);
  const [committed, setCommittedState] = useState<CommittedSelection | null>(null);
  // Latest values readable from callbacks, so a commit right after a setter never sees stale state.
  const live = useRef({ data, onCommit, onSync, getCellIdentity, rect, committed });

  useEffect(() => {
    live.current.data = data;
    live.current.onCommit = onCommit;
    live.current.onSync = onSync;
    live.current.getCellIdentity = getCellIdentity;
  }, [data, onCommit, onSync, getCellIdentity]);

  const setRect = useCallback((next: Selection | null) => {
    live.current.rect = next;
    setRectState(next);
  }, []);

  // Recording the selection already held changes nothing, so a repeated press never re-renders the sheet.
  const setCommitted = useCallback((next: CommittedSelection | null) => {
    if (!sameCommitted(live.current.committed, next)) {
      live.current.committed = next;
      setCommittedState(next);
    }
  }, []);

  // The snapshot the sheet last handed out. An unchanged selection stays the same object, which is how the
  // host tells that a gesture (such as the second half of a double click) changed nothing.
  const published = useRef<SpreadsheetSelectionSnapshot | null>(null);
  const publish = useCallback((next: SpreadsheetSelectionSnapshot | null) => {
    if (!sameSnapshot(published.current, next)) {
      published.current = next;
    }
    return published.current;
  }, []);

  // Holds an event's selection and returns the snapshot for the host. Handing it over inside the event
  // lets the host update in the same render as the sheet instead of one render later.
  const hold = useCallback(
    (next: CommittedSelection | null) => {
      setCommitted(next);
      return publish(resolveSelection(next, live.current.data, live.current.getCellIdentity).snapshot);
    },
    [setCommitted, publish]
  );

  const record = useCallback(
    (next: CommittedSelection | null) => {
      const held = hold(next);
      live.current.onCommit?.(held);
    },
    [hold]
  );

  const resolved = useMemo(() => resolveSelection(committed, data, live.current.getCellIdentity), [committed, data]);
  const snapshot = useMemo(() => publish(resolved.snapshot), [publish, resolved.snapshot]);
  const trustedRect = resolved.rectIntact ? rect : null;

  const extras = useMemo(() => {
    if (!trustedRect) {
      return resolved.positions;
    }
    const outside = new Map<string, Location>();
    resolved.positions.forEach((at, id) => {
      if (!containsCell(trustedRect, at.i, at.j)) {
        outside.set(id, at);
      }
    });
    return outside;
  }, [resolved.positions, trustedRect]);

  const extraKeys = useMemo(() => new Set(Array.from(extras.values(), l => cellKey(l.i, l.j))), [extras]);
  const cells = useMemo(() => buildDescriptors(data, trustedRect, extras), [data, trustedRect, extras]);

  const isSelected = useCallback(
    (i: number, j: number) => extraKeys.has(cellKey(i, j)) || containsCell(trustedRect, i, j),
    [extraKeys, trustedRect]
  );

  const commit = useCallback(
    (gesture: SelectionGesture = 'replace') => {
      const { data: grid, rect: liveRect, committed: prior, getCellIdentity: identityOf } = live.current;
      const identities = new Map<string, Location>();
      if (gesture === 'toggle' && prior) {
        resolveSelection(prior, grid, identityOf).positions.forEach((at, id) => {
          if (!prior.rectIdentities.has(id)) {
            identities.set(id, at);
          }
        });
      }
      const rectIdentities = identitiesIn(grid, liveRect, identityOf);
      rectIdentities.forEach((at, id) => identities.set(id, at));
      record(committedFrom(identities, new Set(rectIdentities.keys()), liveRect, gesture));
    },
    [record]
  );

  const ctrlToggle = useCallback(
    (i: number, j: number) => {
      const { data: grid, rect: liveRect, committed: prior, getCellIdentity: identityOf } = live.current;
      const cell = grid[i]?.[j];
      const id = isSelectable(cell) ? identityOf(cell) : '';
      if (!id) {
        return true;
      }
      const { positions } = resolveSelection(prior, grid, identityOf);
      const inRect = containsCell(liveRect, i, j);
      const identities = new Map(positions);
      // A toggle always folds the whole live rectangle in: that is what the user sees highlighted,
      // however much of it the last commit happened to record.
      const rectFold = identitiesIn(grid, liveRect, identityOf);
      if (!positions.has(id) && !inRect) {
        rectFold.forEach((at, key) => identities.set(key, at));
        identities.set(id, { i, j });
        const single = { start: { i, j }, end: { i, j } };
        setRect(single);
        record(committedFrom(identities, new Set([id]), single, 'toggle'));
        return true;
      }
      if (inRect || prior?.rectIdentities.has(id)) {
        rectFold.forEach((at, key) => identities.set(key, at));
        identities.delete(id);
        setRect(null);
        record(committedFrom(identities, NO_IDENTITIES, null, 'toggle'));
        return identities.size > 0;
      }
      identities.delete(id);
      record(committedFrom(identities, prior?.rectIdentities ?? NO_IDENTITIES, prior?.rect ?? null, 'toggle'));
      return identities.size > 0;
    },
    [record, setRect]
  );

  const clearExtras = useCallback(() => {
    const { data: grid, rect: liveRect, committed: prior, getCellIdentity: identityOf } = live.current;
    if (!prior) {
      return;
    }
    const rectIdentities = identitiesIn(grid, liveRect, identityOf);
    const held = hold(committedFrom(rectIdentities, new Set(rectIdentities.keys()), liveRect, 'replace'));
    live.current.onSync?.(held);
  }, [hold]);

  const clearAll = useCallback(() => {
    setRect(null);
    record(null);
  }, [record, setRect]);

  const selectedCells = useCallback(() => cells, [cells]);

  return {
    rect,
    rectIntact: resolved.rectIntact,
    extras,
    snapshot,
    isSelected,
    hasMultiSelection: cells.length > 1,
    setRect,
    ctrlToggle,
    clearExtras,
    clearAll,
    commit,
    selectedCells,
  };
};

export { useSpreadsheetSelection };
export default useSpreadsheetSelection;
